import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Icon, Input, Message, Modal } from "semantic-ui-react";
import { selectCurrentToken } from "../auth/authSlice";
import { showToast } from "../../app/uiSlice";
import { uploadFileToS3Presigned } from "../../utils/presignedUpload";
import { useCreateSupportTicketMutation } from "./supportTicketApiSlice";

const buildContext = (location) => {
  const sessionMatch = location.pathname.match(/\/chatsessions\/([^/]+)/);
  return {
    route: location.pathname,
    search: location.search,
    session_id: sessionMatch ? sessionMatch[1] : null,
    user_agent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
};

const SupportFeedbackModal = ({ open, onClose }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();

  const context = useMemo(() => buildContext(location), [location]);

  useEffect(() => {
    if (!open) {
      setBody("");
      setImageFile(null);
      setPreviewUrl(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!body.trim()) {
      setError("내용을 입력해 주세요.");
      return;
    }

    setError(null);
    let imageS3Key = null;

    if (imageFile) {
      const uploadResult = await uploadFileToS3Presigned(imageFile, token);
      if (!uploadResult.success) {
        setError(uploadResult.error || "이미지 업로드에 실패했습니다.");
        return;
      }
      imageS3Key = uploadResult.key;
    }

    const payload = {
      body: body.trim(),
      context,
      ...(imageS3Key ? { image_s3_key: imageS3Key } : {}),
    };

    const result = await createSupportTicket(payload);
    if (result.error) {
      const message =
        result.error?.data?.body?.[0]
        || result.error?.data?.image_s3_key?.[0]
        || result.error?.data?.detail
        || "피드백 제출에 실패했습니다.";
      setError(message);
      return;
    }

    dispatch(showToast({
      title: "피드백이 제출되었습니다",
      message: "관리자 확인 후 답변을 받을 수 있습니다.",
      type: "success",
    }));
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>피드백 보내기</span>
        <Icon name="close" style={{ cursor: "pointer" }} onClick={handleClose} />
      </Modal.Header>
      <Modal.Content>
        <Form error={!!error} onSubmit={(event) => event.preventDefault()}>
          <Form.Field required>
            <label>내용</label>
            <Form.TextArea
              placeholder="문제 상황이나 개선 요청을 입력해 주세요."
              value={body}
              onChange={(event, { value }) => setBody(value)}
              rows={5}
            />
          </Form.Field>
          <Form.Field>
            <label>스크린샷 (선택)</label>
            <Input
              type="file"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
          </Form.Field>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="첨부 미리보기"
              style={{ maxWidth: "100%", maxHeight: 200, marginTop: 8, borderRadius: 8 }}
            />
          )}
          {error && <Message error content={error} />}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={isLoading}>
          취소
        </Button>
        <Button primary loading={isLoading} disabled={isLoading} onClick={handleSubmit}>
          제출
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default SupportFeedbackModal;
